import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Megaphone, Mail, Users, Building2, Globe } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { useToast } from '../components/Toaster.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface OrgLite {
  id: string;
  name: string;
}

interface BroadcastSummary {
  id: string;
  emailSubject: string | null;
  emailBodyText: string | null;
  bannerSubject: string | null;
  bannerBodyText: string | null;
  sentAt: string;
  sentByName: string;
  targetAgents: boolean;
  targetContacts: boolean;
  targetOrganizationIds: string[] | null;
  recipientCount: number;
  bannerExpiresAt: string | null;
}

type ContactScope = 'all' | 'specific';

export function AdminBroadcastsPage(): React.ReactElement {
  useDocumentTitle('Broadcasts');
  const { agent } = useAuth();
  const toast = useToast();

  const [orgs, setOrgs] = useState<OrgLite[]>([]);
  const [recent, setRecent] = useState<BroadcastSummary[] | null>(null);

  const [targetAgents, setTargetAgents] = useState(true);
  const [targetContacts, setTargetContacts] = useState(false);
  const [contactScope, setContactScope] = useState<ContactScope>('all');
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<string>>(new Set());

  const [sendEmail, setSendEmail] = useState(true);
  const [sendBanner, setSendBanner] = useState(true);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBodyText, setEmailBodyText] = useState('');
  const [bannerSubject, setBannerSubject] = useState('');
  const [bannerBodyText, setBannerBodyText] = useState('');
  const [bannerExpires, setBannerExpires] = useState(''); // yyyy-mm-dd, optional

  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  async function loadOrgs() {
    try {
      const r = await apiFetch<{ organizations: OrgLite[] }>('/api/organizations');
      setOrgs(r.organizations);
    } catch {
      // best effort
    }
  }

  async function loadRecent() {
    try {
      const r = await apiFetch<{ broadcasts: BroadcastSummary[] }>('/api/admin/broadcasts');
      setRecent(r.broadcasts);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) return;
      toast.error(e instanceof ApiError ? e.message : 'Failed to load recent broadcasts.');
    }
  }

  useEffect(() => {
    void loadOrgs();
    void loadRecent();
  }, []);

  if (!agent || agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  function audiencePayload() {
    const orgIds =
      targetContacts && contactScope === 'specific' ? Array.from(selectedOrgIds) : null;
    return {
      targetAgents,
      targetContacts,
      targetOrganizationIds: orgIds,
    };
  }

  async function preview() {
    setRecipientCount(null);
    if (!targetAgents && !targetContacts) {
      toast.error('Pick at least one audience.');
      return;
    }
    if (targetContacts && contactScope === 'specific' && selectedOrgIds.size === 0) {
      toast.error('Pick at least one organization, or switch to "all client contacts".');
      return;
    }
    setPreviewing(true);
    try {
      const r = await apiFetch<{ recipientCount: number }>(
        '/api/admin/broadcasts/preview',
        { method: 'POST', json: audiencePayload() },
      );
      setRecipientCount(r.recipientCount);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  }

  async function send() {
    if (!sendEmail && !sendBanner) {
      toast.error('Pick at least one channel (email or banner).');
      return;
    }
    if (sendEmail && (!emailSubject.trim() || !emailBodyText.trim())) {
      toast.error('Email subject and message are required.');
      return;
    }
    if (sendBanner && (!bannerSubject.trim() || !bannerBodyText.trim())) {
      toast.error('Banner subject and message are required.');
      return;
    }
    if (!targetAgents && !targetContacts) {
      toast.error('Pick at least one audience.');
      return;
    }
    if (targetContacts && contactScope === 'specific' && selectedOrgIds.size === 0) {
      toast.error('Pick at least one organization, or switch to "all client contacts".');
      return;
    }
    // Auto-preview if the admin hasn't clicked "Check recipients" — saves
    // a step for the common "just send it" flow.
    let count = recipientCount;
    if (count === null) {
      setSending(true);
      try {
        const r = await apiFetch<{ recipientCount: number }>(
          '/api/admin/broadcasts/preview',
          { method: 'POST', json: audiencePayload() },
        );
        count = r.recipientCount;
        setRecipientCount(count);
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : 'Failed to resolve recipients.');
        setSending(false);
        return;
      }
      setSending(false);
    }
    if (count === 0) {
      toast.error('No recipients match this audience.');
      return;
    }
    const expiresIso = bannerExpires
      ? new Date(`${bannerExpires}T23:59:59`).toISOString()
      : null;
    const channels: string[] = [];
    if (sendEmail) channels.push('email');
    if (sendBanner) channels.push('in-app banner');
    const previewSubject = sendEmail ? emailSubject.trim() : bannerSubject.trim();
    const confirmed = confirm(
      `Send "${previewSubject}" to ${count} recipient${count === 1 ? '' : 's'} via ${channels.join(' + ')}?`,
    );
    if (!confirmed) return;
    setSending(true);
    try {
      const r = await apiFetch<{ sent: number; failed: number }>('/api/admin/broadcasts', {
        method: 'POST',
        json: {
          ...audiencePayload(),
          sendEmail,
          sendBanner,
          emailSubject: sendEmail ? emailSubject.trim() : undefined,
          emailBodyText: sendEmail ? emailBodyText.trim() : undefined,
          bannerSubject: sendBanner ? bannerSubject.trim() : undefined,
          bannerBodyText: sendBanner ? bannerBodyText.trim() : undefined,
          bannerExpiresAt: sendBanner ? expiresIso : null,
        },
      });
      if (sendEmail) {
        if (r.failed > 0) {
          toast.info(`Email sent to ${r.sent}, ${r.failed} failed (see logs).`);
        } else {
          toast.success(`Sent to ${r.sent} recipient${r.sent === 1 ? '' : 's'}.`);
        }
      } else {
        toast.success(`Banner posted to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}.`);
      }
      setEmailSubject('');
      setEmailBodyText('');
      setBannerSubject('');
      setBannerBodyText('');
      setBannerExpires('');
      setRecipientCount(null);
      await loadRecent();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Send failed.');
    } finally {
      setSending(false);
    }
  }

  function toggleOrg(id: string) {
    setSelectedOrgIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setRecipientCount(null);
  }

  function audienceLabel(b: BroadcastSummary): string {
    const parts: string[] = [];
    if (b.targetAgents) parts.push('all agents');
    if (b.targetContacts) {
      if (b.targetOrganizationIds && b.targetOrganizationIds.length > 0) {
        parts.push(`contacts of ${b.targetOrganizationIds.length} org${b.targetOrganizationIds.length === 1 ? '' : 's'}`);
      } else {
        parts.push('all client contacts');
      }
    }
    return parts.join(' + ');
  }

  function channelLabel(b: BroadcastSummary): string {
    const parts: string[] = [];
    if (b.emailSubject) parts.push('email');
    if (b.bannerSubject) parts.push('banner');
    return parts.join(' + ');
  }

  function isBannerActive(b: BroadcastSummary): boolean {
    if (!b.bannerSubject) return false;
    if (!b.bannerExpiresAt) return true;
    return new Date(b.bannerExpiresAt).getTime() > Date.now();
  }

  async function endBanner(id: string) {
    if (!confirm('Take this banner down for all recipients now?')) return;
    try {
      await apiFetch(`/api/admin/broadcasts/${id}/end-banner`, { method: 'POST' });
      toast.success('Banner ended.');
      await loadRecent();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not end banner.');
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-2xl font-bold text-[var(--color-header)]">Broadcasts</h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Send an announcement by email, as a dismissible in-app banner, or both. Pick the audience
          (agents and/or client contacts), then compose the content for each channel you enable.
        </p>
      </div>

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Compose</h2>
        <div className="mt-4 space-y-5">
          <div>
            <span className="mb-2 block text-sm font-medium text-[var(--color-header)]">
              Audience
            </span>
            <label className="mb-2 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={targetAgents}
                onChange={(e) => {
                  setTargetAgents(e.target.checked);
                  setRecipientCount(null);
                }}
                className="mt-0.5"
              />
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-4 w-4 text-[var(--color-t3-blue)]" /> All active agents
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={targetContacts}
                onChange={(e) => {
                  setTargetContacts(e.target.checked);
                  setRecipientCount(null);
                }}
                className="mt-0.5"
              />
              <span className="inline-flex items-center gap-1.5">
                <Building2 className="h-4 w-4 text-[var(--color-t3-purple)]" /> Client contacts
              </span>
            </label>
            {targetContacts && (
              <div className="mt-3 ml-6 space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="contactScope"
                    value="all"
                    checked={contactScope === 'all'}
                    onChange={() => {
                      setContactScope('all');
                      setRecipientCount(null);
                    }}
                  />
                  <span className="inline-flex items-center gap-1.5">
                    <Globe className="h-3.5 w-3.5 text-[var(--color-muted)]" /> All organizations
                  </span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="contactScope"
                    value="specific"
                    checked={contactScope === 'specific'}
                    onChange={() => {
                      setContactScope('specific');
                      setRecipientCount(null);
                    }}
                  />
                  <span>Specific organizations</span>
                </label>
                {contactScope === 'specific' && (
                  <div className="mt-1 max-h-48 overflow-auto rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-2 text-sm">
                    {orgs.length === 0 ? (
                      <p className="px-2 py-1 text-[var(--color-muted)]">No organizations loaded.</p>
                    ) : (
                      orgs.map((o) => (
                        <label key={o.id} className="flex items-center gap-2 px-1 py-1">
                          <input
                            type="checkbox"
                            checked={selectedOrgIds.has(o.id)}
                            onChange={() => toggleOrg(o.id)}
                          />
                          <span>{o.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <span className="mb-2 block text-sm font-medium text-[var(--color-header)]">
              Channels
            </span>
            <label className="mb-2 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="mt-0.5"
              />
              <span className="inline-flex items-center gap-1.5">
                <Mail className="h-4 w-4 text-[var(--color-t3-blue)]" /> Email
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={sendBanner}
                onChange={(e) => setSendBanner(e.target.checked)}
                className="mt-0.5"
              />
              <span className="inline-flex items-center gap-1.5">
                <Megaphone className="h-4 w-4 text-[var(--color-t3-purple)]" /> In-app banner
              </span>
            </label>
          </div>

          {sendEmail && (
            <div className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
              <h3 className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-header)]">
                <Mail className="h-4 w-4 text-[var(--color-t3-blue)]" /> Email
              </h3>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">Subject</span>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  maxLength={200}
                  placeholder="Maintenance window — Thu May 15, 9pm ET"
                  className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2.5 text-[15px]"
                />
              </label>
              <label className="mt-3 block">
                <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">Message</span>
                <textarea
                  value={emailBodyText}
                  onChange={(e) => setEmailBodyText(e.target.value)}
                  maxLength={20000}
                  rows={8}
                  placeholder="Plain text. Blank lines become paragraphs in the email."
                  className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2.5 text-[15px]"
                />
              </label>
            </div>
          )}

          {sendBanner && (
            <div className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
              <h3 className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-header)]">
                <Megaphone className="h-4 w-4 text-[var(--color-t3-purple)]" /> In-app banner
              </h3>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">Headline</span>
                <input
                  type="text"
                  value={bannerSubject}
                  onChange={(e) => setBannerSubject(e.target.value)}
                  maxLength={200}
                  placeholder="Scheduled maintenance Thursday 9pm ET"
                  className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2.5 text-[15px]"
                />
              </label>
              <label className="mt-3 block">
                <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">Body</span>
                <textarea
                  value={bannerBodyText}
                  onChange={(e) => setBannerBodyText(e.target.value)}
                  maxLength={2000}
                  rows={3}
                  placeholder="Short version that fits in a banner strip. Keep it terse."
                  className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2.5 text-[15px]"
                />
                <span className="mt-1 block text-xs text-[var(--color-muted)]">Max 2000 characters.</span>
              </label>
              <label className="mt-3 block">
                <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                  Banner expires (optional)
                </span>
                <input
                  type="date"
                  value={bannerExpires}
                  onChange={(e) => setBannerExpires(e.target.value)}
                  className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-[15px]"
                />
                <span className="mt-1 block text-xs text-[var(--color-muted)]">
                  The banner disappears for everyone after this date. Leave blank to keep showing
                  until each user dismisses it.
                </span>
              </label>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 border-t border-[var(--color-border)] pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => void preview()}
              disabled={previewing || sending}
            >
              {previewing ? 'Checking…' : 'Check recipients'}
            </Button>
            {recipientCount !== null && (
              <span className="text-sm text-[var(--color-body)]">
                <strong>{recipientCount}</strong> recipient{recipientCount === 1 ? '' : 's'}
              </span>
            )}
            <div className="ml-auto">
              <Button
                type="button"
                onClick={() => void send()}
                disabled={sending || recipientCount === 0}
              >
                {sending ? 'Sending…' : 'Send broadcast'}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Recent broadcasts</h2>
        {recent === null ? (
          <p className="mt-3 text-sm text-[var(--color-muted)]">Loading…</p>
        ) : recent.length === 0 ? (
          <p className="mt-3 text-sm text-[var(--color-muted)]">No broadcasts sent yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-[var(--color-border)]">
            {recent.map((b) => {
              const displaySubject = b.emailSubject ?? b.bannerSubject ?? '(no subject)';
              return (
                <li key={b.id} className="py-3">
                  <div className="flex items-start gap-3">
                    <Megaphone className="mt-0.5 h-4 w-4 text-[var(--color-t3-blue)]" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[var(--color-header)]">{displaySubject}</div>
                      {b.emailSubject && (
                        <div className="mt-1 text-xs uppercase tracking-wide text-[var(--color-muted)]">
                          Email
                        </div>
                      )}
                      {b.emailBodyText && (
                        <div className="mt-0.5 whitespace-pre-wrap text-sm text-[var(--color-body)]">
                          {b.emailBodyText}
                        </div>
                      )}
                      {b.bannerSubject && (
                        <div className="mt-2 text-xs uppercase tracking-wide text-[var(--color-muted)]">
                          Banner — {b.bannerSubject}
                        </div>
                      )}
                      {b.bannerBodyText && (
                        <div className="mt-0.5 whitespace-pre-wrap text-sm text-[var(--color-body)]">
                          {b.bannerBodyText}
                        </div>
                      )}
                      <div className="mt-2 text-xs text-[var(--color-muted)]">
                        Sent {new Date(b.sentAt).toLocaleString()} by {b.sentByName} ·{' '}
                        {channelLabel(b)} · {audienceLabel(b)} · {b.recipientCount} recipient
                        {b.recipientCount === 1 ? '' : 's'}
                        {b.bannerExpiresAt
                          ? ` · banner ${isBannerActive(b) ? 'expires' : 'expired'} ${new Date(b.bannerExpiresAt).toLocaleString()}`
                          : ''}
                      </div>
                      {isBannerActive(b) && (
                        <div className="mt-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => void endBanner(b.id)}
                          >
                            End banner now
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
